import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { detailPending, detailSettled } from "helpers/detailRequest";
import apiAxios from "helpers/apiAxios";
import { handleAsyncThunkError, addRejectedMatcher } from "../reduxHelpers";

const BASE = "/erp/purchase_requests";

export const getPurchaseRequests = createAsyncThunk(
  "PurchaseRequest/getPurchaseRequests",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(BASE, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load purchase requisitions."));
    }
  }
);

export const getPurchaseRequestInbox = createAsyncThunk(
  "PurchaseRequest/getPurchaseRequestInbox",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/inbox`, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load approval inbox."));
    }
  }
);

export const getPurchaseRequest = createAsyncThunk(
  "PurchaseRequest/getPurchaseRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${id}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load purchase requisition."));
    }
  }
);

export const createPurchaseRequest = createAsyncThunk(
  "PurchaseRequest/createPurchaseRequest",
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(BASE, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to create purchase requisition."));
    }
  }
);

export const updatePurchaseRequest = createAsyncThunk(
  "PurchaseRequest/updatePurchaseRequest",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.put(`${BASE}/${id}`, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to update purchase requisition."));
    }
  }
);

export const deletePurchaseRequest = createAsyncThunk(
  "PurchaseRequest/deletePurchaseRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.delete(`${BASE}/${id}`);
      return { id, ...res.data };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to delete purchase requisition."));
    }
  }
);

export const submitPurchaseRequest = createAsyncThunk(
  "PurchaseRequest/submitPurchaseRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/submit`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to submit for approval."));
    }
  }
);

export const withdrawPurchaseRequest = createAsyncThunk(
  "PurchaseRequest/withdrawPurchaseRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/withdraw`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to withdraw the request."));
    }
  }
);

export const approvePurchaseRequest = createAsyncThunk(
  "PurchaseRequest/approvePurchaseRequest",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/approve`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Approval failed."));
    }
  }
);

export const rejectPurchaseRequest = createAsyncThunk(
  "PurchaseRequest/rejectPurchaseRequest",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/reject`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Rejection failed."));
    }
  }
);

// Cancel — người lập tự huỷ hẳn phiếu (draft/pending), khác Withdraw (rút về draft để sửa).
export const cancelPurchaseRequest = createAsyncThunk(
  "PurchaseRequest/cancelPurchaseRequest",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/cancel`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Cancel failed."));
    }
  }
);

// Duplicate — tạo 1 PR mới ở Draft, copy nội dung từ phiếu này.
export const duplicatePurchaseRequest = createAsyncThunk(
  "PurchaseRequest/duplicatePurchaseRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/duplicate`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Duplicate failed."));
    }
  }
);

export const uploadPurchaseRequestDocument = createAsyncThunk(
  "PurchaseRequest/uploadPurchaseRequestDocument",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/upload_document`, formData);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Document upload failed."));
    }
  }
);

// Nguồn picker chọn Head of Dept (dropdown user, mọi user).
export const searchPurchaseRequestUsers = async (q) => {
  const res = await apiAxios.get(`${BASE}/user_search`, { params: { q } });
  return res.data;
};

// Bản in: backend sinh PDF theo mẫu QF-LGS-01/QF-PUR-01 (có chữ ký), mở tab mới để xem/in.
// timeout dài hơn mặc định (30s) vì server phải chạy wkhtmltopdf + tải ảnh chữ ký
// từng người ký từ SignHub — có thể mất hơn 30s khi PR có nhiều bước ký hoặc SignHub chậm.
export const openPurchaseRequestPdf = async (id) => {
  const res = await apiAxios.get(`${BASE}/${id}/print`, { responseType: "blob", timeout: 120000 });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
  const win = window.open(url, "_blank");
  if (!win) {
    const cd = res.headers?.["content-disposition"] || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : `purchase-request-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// Excel: fill mẫu .xls tĩnh (app/templates/erp-pr-po) theo form_type rồi tải về.
export const downloadPurchaseRequestExcel = async (id) => {
  const res = await apiAxios.get(`${BASE}/${id}/print_excel`, { responseType: "blob", timeout: 120000 });
  const cd = res.headers?.["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.ms-excel" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : `purchase-request-${id}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// Nguồn dropdown chọn Site (HO, VTF, VTF1, ...).
export const getPurchaseRequestSiteOptions = async () => {
  const res = await apiAxios.get(`${BASE}/site_options`);
  return res.data;
};

const initialState = {
  allData: { rows: [], total_rows: 0 },
  inboxData: { rows: [], total_rows: 0 },
  recordData: {},
  loading: false,
  error: null,
};

const purchaseRequestSlice = createSlice({
  name: "PurchaseRequest",
  initialState,
  reducers: {
    clearPurchaseRequest: (state) => {
      state.recordData = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseRequests.pending, (state) => { state.loading = true; })
      .addCase(getPurchaseRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.allData = action.payload;
      })
      .addCase(getPurchaseRequestInbox.fulfilled, (state, action) => {
        state.inboxData = action.payload;
      })
      .addCase(getPurchaseRequest.pending, (state, action) => {
        state.loading = true;
        detailPending()(state, action);
      })
      .addCase(getPurchaseRequest.fulfilled, detailSettled((state, action) => {
        state.loading = false;
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      }))
      .addCase(getPurchaseRequest.rejected, detailSettled())
      .addCase(createPurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(updatePurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(submitPurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(withdrawPurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(approvePurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(rejectPurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(cancelPurchaseRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      });

    addRejectedMatcher(builder, "PurchaseRequest");
  },
});

export const { clearPurchaseRequest } = purchaseRequestSlice.actions;
export default purchaseRequestSlice.reducer;
